import React from 'react';
import { Button, Dialog, DotLoading, NavBar, Space, SpinLoading } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MyWhatsAppAccountBinding, WhatsAppAuthMode, WhatsAppConnectionState } from '../../../api/whatsapp';
import { profileWhatsAppAccountsPageStyles } from './styles';
import { ProfileWhatsAppAccountsState } from './types';

interface ProfileWhatsAppAccountsViewProps {
  state: ProfileWhatsAppAccountsState;
  bindings: MyWhatsAppAccountBinding[];
  connect: {
    visible: boolean;
    binding: MyWhatsAppAccountBinding | null;
    status: 'idle' | 'loading' | 'qr_pending' | 'pairing_code_pending' | 'connected' | 'error';
    phase: WhatsAppConnectionState['status'] | null;
    authMode: WhatsAppAuthMode;
    qrImage: string | null;
    pairingCode: string | null;
    pairingCodeExpiresInMs: number | null;
    errorMessage: string;
    verifying: boolean;
    canUsePairingCode: boolean;
  };
  onSetDefault: (bindingId: string) => Promise<void>;
  onUnbind: (bindingId: string) => Promise<void>;
  onOpenConnect: (bindingId: string) => Promise<void> | void;
  onCloseConnect: () => void;
  onRequestConnect: (options?: { mode?: WhatsAppAuthMode }) => Promise<void>;
}

export const ProfileWhatsAppAccountsView: React.FC<ProfileWhatsAppAccountsViewProps> = ({
  state,
  bindings,
  connect,
  onSetDefault,
  onUnbind,
  onOpenConnect,
  onCloseConnect,
  onRequestConnect,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('profile');

  const activeBinding = connect.binding;
  const connectTitle = activeBinding
    ? t('waAccounts.connectDialogTitle', { phone: activeBinding.account.phone_number })
    : t('waAccounts.connectDialogTitleFallback');
  const connectRetryLabel = connect.authMode === 'pairing_code'
    ? t('waAccounts.connectRetryPairing')
    : t('waAccounts.connectRetryQr');
  const connectSwitchLabel = connect.authMode === 'pairing_code'
    ? t('waAccounts.connectSwitchToQr')
    : t('waAccounts.connectSwitchToPairing');
  const connectExpiresLabel = typeof connect.pairingCodeExpiresInMs === 'number' && connect.pairingCodeExpiresInMs > 0
    ? t('waAccounts.connectPairingExpires', { seconds: Math.max(1, Math.ceil(connect.pairingCodeExpiresInMs / 1000)) })
    : '';
  const isConnectBusy = connect.verifying || connect.status === 'loading';

  return (
    <div className="webview-page" style={profileWhatsAppAccountsPageStyles.page}>
      <NavBar
        onBack={() => navigate('/profile')}
        right={(
          <button
            type="button"
            style={profileWhatsAppAccountsPageStyles.plusButton}
            onClick={() => navigate('/profile/whatsapp-accounts/new')}
            aria-label={t('waAccounts.addAction')}
          >
            +
          </button>
        )}
      >
        {t('waAccounts.navTitle')}
      </NavBar>

      <Dialog
        visible={connect.visible}
        title={connectTitle}
        actions={[
          [
            {
              key: 'close',
              text: t('waAccounts.connectClose'),
              onClick: onCloseConnect,
              style: {
                color: '#4e5969',
                background: '#f7f8fa',
                borderRight: '1px solid rgba(229, 234, 243, 0.9)',
                fontWeight: 600,
                opacity: 1,
              } as React.CSSProperties,
            },
            {
              key: 'retry',
              text: connectRetryLabel,
              onClick: async () => onRequestConnect({ mode: connect.authMode }),
              disabled: isConnectBusy,
              bold: true,
              style: {
                color: isConnectBusy ? '#b9c3d9' : '#ffffff',
                background: isConnectBusy ? '#e8edf7' : 'linear-gradient(135deg, #4d8dff 0%, #1769ff 100%)',
                fontWeight: 700,
                opacity: isConnectBusy ? 0.9 : 1,
              } as React.CSSProperties,
            },
          ],
        ]}
        content={
          <div style={{ width: '100%' }}>
            <Space direction="vertical" block style={{ width: '100%' }}>
              {activeBinding ? (
                <div className="webview-micro" style={{ color: '#7f879b' }}>
                  {t('waAccounts.connectDialogSubtitle', { phone: activeBinding.account.phone_number })}
                </div>
              ) : null}

              {connect.canUsePairingCode ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="mini"
                    fill="outline"
                    disabled={connect.verifying || connect.status === 'loading'}
                    style={profileWhatsAppAccountsPageStyles.modeSwitchButton}
                    onClick={async () => onRequestConnect({ mode: connect.authMode === 'pairing_code' ? 'qr' : 'pairing_code' })}
                  >
                    {connectSwitchLabel}
                  </Button>
                </div>
              ) : null}

              {connect.status === 'loading' ? (
                <div style={profileWhatsAppAccountsPageStyles.connectCenter}>
                  <SpinLoading style={{ '--size': '26px' }} />
                  <div style={{ marginTop: 10, fontSize: 13, color: '#7f879b', textAlign: 'center', lineHeight: 1.5 }}>
                    {t('waAccounts.connectLoading', { phase: connect.phase || '-' })}
                  </div>
                </div>
              ) : null}

              {connect.status === 'pairing_code_pending' ? (
                <div style={profileWhatsAppAccountsPageStyles.connectCenter}>
                  <div style={profileWhatsAppAccountsPageStyles.pairingCodeBox}>
                    <div style={profileWhatsAppAccountsPageStyles.pairingCodeText}>{connect.pairingCode || '-'}</div>
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, color: '#7f879b', textAlign: 'center', lineHeight: 1.55 }}>
                    {t('waAccounts.connectPairingHint')}
                  </div>
                  {connectExpiresLabel ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#9aa3b8', textAlign: 'center' }}>
                      {connectExpiresLabel}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {connect.status === 'qr_pending' ? (
                <div style={profileWhatsAppAccountsPageStyles.connectCenter}>
                  <div style={profileWhatsAppAccountsPageStyles.qrBox}>
                    {connect.qrImage ? (
                      <img alt="qr" src={connect.qrImage} style={profileWhatsAppAccountsPageStyles.qrImage} />
                    ) : (
                      <SpinLoading style={{ '--size': '26px' }} />
                    )}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, color: '#7f879b', textAlign: 'center', lineHeight: 1.55 }}>
                    {t('waAccounts.connectQrHint')}
                  </div>
                </div>
              ) : null}

              {connect.status === 'connected' ? (
                <div style={profileWhatsAppAccountsPageStyles.connectCenter}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#2cb67d' }}>
                    {t('waAccounts.connectConnected')}
                  </div>
                  {connect.verifying ? (
                    <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7f879b' }}>
                      <SpinLoading style={{ '--size': '16px' }} />
                      <span style={{ fontSize: 12 }}>{t('waAccounts.connectVerifying')}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {connect.status === 'error' ? (
                <div style={profileWhatsAppAccountsPageStyles.connectErrorBox}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#d64c4c' }}>{t('waAccounts.connectErrorTitle')}</div>
                  <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.55, color: '#7f879b' }}>
                    {connect.errorMessage || t('waAccounts.connectFailed')}
                  </div>
                </div>
              ) : null}
            </Space>
          </div>
        }
        onClose={onCloseConnect}
      />

      {state.loading ? (
        <div className="webview-section" style={{ display: 'flex', justifyContent: 'center', padding: '18px 0' }}>
          <DotLoading />
        </div>
      ) : (
        <>
          <div className="webview-section">
            <div style={profileWhatsAppAccountsPageStyles.sectionCard}>
              <div style={profileWhatsAppAccountsPageStyles.sectionTitle}>{t('waAccounts.listTitle')}</div>
              <div style={profileWhatsAppAccountsPageStyles.sectionSubtitle}>{t('waAccounts.listSubtitle')}</div>

              <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
                {bindings.length === 0 ? (
                  <div className="webview-micro" style={{ textAlign: 'center', padding: '18px 0', color: '#7f879b' }}>
                    {t('waAccounts.empty')}
                  </div>
                ) : (
                  bindings.map((binding) => {
                    const isDefault = Boolean(binding.is_default);
                    const isVerified = Boolean(binding.verified_at);
                    const connectionStatus = String(binding.connection_status || 'offline').toUpperCase();
                    const accountStatus = String(binding.account?.status || '-').toUpperCase();
                    const connectionStatusTone = resolveStatusTone(connectionStatus);
                    const accountStatusTone = resolveStatusTone(accountStatus);

                    return (
                      <div key={binding.id} style={profileWhatsAppAccountsPageStyles.accountCard}>
                        <div style={profileWhatsAppAccountsPageStyles.accountTop}>
                          <div style={profileWhatsAppAccountsPageStyles.accountIconWrap}>
                            <PhoneGlyph />
                          </div>
                          <div style={profileWhatsAppAccountsPageStyles.accountMain}>
                            <div style={profileWhatsAppAccountsPageStyles.phoneText}>{binding.account.phone_number}</div>
                            <div style={profileWhatsAppAccountsPageStyles.aliasRow}>
                              <NoteGlyph />
                              <span>
                                {binding.alias
                                  ? t('waAccounts.aliasLineCompact', { alias: binding.alias })
                                  : t('waAccounts.noAliasCompact')}
                              </span>
                            </div>
                          </div>
                          <div style={profileWhatsAppAccountsPageStyles.rightMeta}>
                            <div style={isVerified ? profileWhatsAppAccountsPageStyles.verifiedBadge : profileWhatsAppAccountsPageStyles.unverifiedBadge}>
                              {isVerified ? t('waAccounts.verifiedTag') : t('waAccounts.unverifiedTag')}
                            </div>
                            {isDefault ? (
                              <div style={profileWhatsAppAccountsPageStyles.defaultText}>{t('waAccounts.defaultTag')}</div>
                            ) : null}
                          </div>
                        </div>

                        <div style={profileWhatsAppAccountsPageStyles.statusRow}>
                          <span
                            style={{
                              ...profileWhatsAppAccountsPageStyles.statusDot,
                              background: connectionStatusTone.dot,
                            }}
                          />
                          <span style={profileWhatsAppAccountsPageStyles.statusLabel}>{t('waAccounts.connectionStatusLabel')}</span>
                          <span
                            style={{
                              ...profileWhatsAppAccountsPageStyles.statusValue,
                              color: connectionStatusTone.text,
                            }}
                          >
                            {connectionStatus}
                          </span>
                        </div>

                        <div style={{ ...profileWhatsAppAccountsPageStyles.statusRow, marginTop: 6 }}>
                          <span
                            style={{
                              ...profileWhatsAppAccountsPageStyles.statusDot,
                              background: accountStatusTone.dot,
                            }}
                          />
                          <span style={profileWhatsAppAccountsPageStyles.statusLabel}>{t('waAccounts.warmupStatusLabel')}</span>
                          <span
                            style={{
                              ...profileWhatsAppAccountsPageStyles.statusValue,
                              color: accountStatusTone.text,
                            }}
                          >
                            {accountStatus}
                          </span>
                        </div>

                        <div style={profileWhatsAppAccountsPageStyles.actionsRow}>
                          <Button
                            size="small"
                            disabled={state.submitting || connect.visible}
                            style={profileWhatsAppAccountsPageStyles.primaryActionButton}
                            onClick={() => void onOpenConnect(binding.id)}
                          >
                            <span style={profileWhatsAppAccountsPageStyles.actionContent}>
                              <RefreshGlyph />
                              <span>{isVerified ? t('waAccounts.reconnect') : t('waAccounts.connect')}</span>
                            </span>
                          </Button>
                          {!isDefault ? (
                            <Button
                              size="small"
                              disabled={state.submitting}
                              fill="none"
                              style={profileWhatsAppAccountsPageStyles.outlineActionButton}
                              onClick={() => void onSetDefault(binding.id)}
                            >
                              <span style={profileWhatsAppAccountsPageStyles.actionContent}>
                                <DefaultGlyph />
                                <span>{t('waAccounts.setDefault')}</span>
                              </span>
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            disabled={state.submitting || connect.visible}
                            fill="none"
                            style={profileWhatsAppAccountsPageStyles.dangerActionButton}
                            onClick={() => void onUnbind(binding.id)}
                          >
                            <span style={profileWhatsAppAccountsPageStyles.actionContent}>
                              <UnbindGlyph />
                              <span>{t('waAccounts.unbind')}</span>
                            </span>
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <Button
                block
                color="primary"
                style={{ ...profileWhatsAppAccountsPageStyles.submitButton, marginTop: 16 }}
                onClick={() => navigate('/profile/whatsapp-accounts/new')}
              >
                {t('waAccounts.bindSubmit')}
              </Button>
            </div>
          </div>

          <div className="webview-section">
            <div style={profileWhatsAppAccountsPageStyles.securityNotice}>
              <ShieldGlyph />
              <div>
                <div style={profileWhatsAppAccountsPageStyles.securityTitle}>{t('waAccounts.securityTitle')}</div>
                <div style={profileWhatsAppAccountsPageStyles.securityText}>{t('waAccounts.securityText')}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function resolveStatusTone(status: string): { dot: string; text: string } {
  const normalizedStatus = status.trim().toLowerCase();
  if (normalizedStatus === 'warming') {
    return {
      dot: '#ff8f6b',
      text: '#f08a5d',
    };
  }

  if (normalizedStatus === 'active' || normalizedStatus === 'online' || normalizedStatus === 'ready' || normalizedStatus === 'connected') {
    return {
      dot: '#3ecf8e',
      text: '#1fa971',
    };
  }

  if (normalizedStatus === 'connecting' || normalizedStatus === 'recovering') {
    return {
      dot: '#6f6cf6',
      text: '#5a57d9',
    };
  }

  if (normalizedStatus === 'need_reauth' || normalizedStatus === 'suspended') {
    return {
      dot: '#e95d5d',
      text: '#d94848',
    };
  }

  return {
    dot: '#b6bdcc',
    text: '#98a2b3',
  };
}

const PhoneGlyph: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.962 3.333h1.821c.37 0 .692.252.78.611l.606 2.46a.833.833 0 0 1-.24.814L7.596 8.55a10.039 10.039 0 0 0 3.854 3.854l1.333-1.333a.833.833 0 0 1 .814-.24l2.46.606a.803.803 0 0 1 .61.78v1.821a1.628 1.628 0 0 1-1.838 1.618A13.508 13.508 0 0 1 4.344 5.171 1.628 1.628 0 0 1 5.962 3.333Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const NoteGlyph: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.25 1.75h4.379L9.75 4.871v5.379a.75.75 0 0 1-.75.75h-6a.75.75 0 0 1-.75-.75v-7.75a.75.75 0 0 1 .75-.75Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
    <path d="M6.5 1.75v2.5a.75.75 0 0 0 .75.75h2.5" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
  </svg>
);

const RefreshGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.833 5.833V2.5m0 0H12.5m3.333 0L12.5 5.833M4.167 14.167V17.5m0 0H7.5m-3.333 0 3.333-3.333" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.56 8.052A5.833 5.833 0 0 1 15.833 5.833m-1.393 6.115A5.833 5.833 0 0 1 4.167 14.167" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DefaultGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 2.917 12.188 7.35l4.895.712-3.542 3.454.836 4.875L10 14.087 5.624 16.39l.836-4.875L2.917 8.062l4.895-.712L10 2.917Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const UnbindGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.083 7.083 12.917 12.917m0-5.834-5.834 5.834" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
    <path d="M6.667 3.333h6.666A3.333 3.333 0 0 1 16.667 6.667v6.666a3.333 3.333 0 0 1-3.334 3.334H6.667a3.333 3.333 0 0 1-3.334-3.334V6.667a3.333 3.333 0 0 1 3.334-3.334Z" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const ShieldGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 2.5 15.833 4.583v4.942c0 3.765-2.433 6.883-5.833 7.975-3.4-1.092-5.833-4.21-5.833-7.975V4.583L10 2.5Z" stroke="#25B79A" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);
